/**
 * The words of generation with AI (document/64): images, videos and clips,
 * calls to action, page sections. Kept apart from dict.ts to keep that file
 * readable; they are spread into the same two trees, so `dict.test.ts`
 * checks them like the rest.
 */
import type { DictNode } from './dict.js'

export const aiGenIt: DictNode = {
  aiGen: {
    common: {
      account: 'Account AI',
      accountHelp: 'Quale abbonamento risponde. Se non scegli, risponde il primo disponibile.',
      accountDefault: 'Predefinito (il primo disponibile)',
      accountsDegraded: 'AI non risponde all’elenco degli account: si userà quello predefinito.',
      accountUnavailable: '{{label}} — non disponibile: {{reason}}',
      prompt: 'Descrizione',
      promptShort: 'Servono almeno 3 caratteri.',
      promptLong: 'La descrizione è troppo lunga: al massimo {{max}} caratteri.',
      aspect: 'Formato',
      failed: 'Generazione non riuscita',
    },
    aspect: {
      r16x9: 'Orizzontale 16:9',
      r4x3: 'Orizzontale 4:3',
      r1x1: 'Quadrato 1:1',
      r3x4: 'Verticale 3:4',
      r9x16: 'Verticale 9:16',
    },
    library: {
      image: 'Genera immagine con AI',
      video: 'Genera video con AI',
    },
    field: {
      generate: 'Genera con AI',
    },
    image: {
      title: 'Genera un’immagine con AI',
      titleVariant: 'Rigenera una variante',
      reason: 'Immagine generata con AI: {{prompt}}',
      promptHelp: 'Descrivi soggetto, stile e luce. Il testo resta salvato insieme all’immagine.',
      name: 'Nome nella libreria',
      nameHelp: 'Facoltativo: se lo lasci vuoto si usano le prime parole della descrizione.',
      alt: 'Testo alternativo ({{lang}})',
      altHelp: 'Facoltativo ma consigliato: dice a chi non vede l’immagine cosa mostra.',
      archivePrevious: 'Archivia l’immagine precedente',
      archivePreviousHelp: 'Se la precedente è già usata in una pagina pubblicata, lasciala attiva: archiviarla la toglie dal prossimo rilascio.',
      generate: 'Genera',
      regenerate: 'Rigenera',
      generating: 'Sto generando…',
      generatingHelp: 'Può richiedere fino a due minuti. Se chiudi la finestra, l’immagine arriva comunque nella libreria.',
      empty: 'L’anteprima compare qui appena l’immagine è pronta.',
      use: 'Usa questa immagine',
      deduplicated: 'AI ha restituito un’immagine già presente nella libreria: non è stato creato un duplicato.',
      promptUsed: 'Descrizione usata:',
      history: 'Cronologia delle varianti',
      earlier: 'prima',
      attempt: 'tentativo {{n}}',
    },
    video: {
      title: 'Genera video o clip con AI',
      reason: 'Video generato con AI: {{prompt}}',
      promptHelp: 'Cosa deve vedersi e come si muove la scena. Una clip breve costa meno attesa di un video lungo.',
      kind: 'Cosa generare',
      kinds: { video: 'Video', clip: 'Clip breve' },
      videoHelp: 'Un video completo: l’attesa può essere di alcuni minuti.',
      clipHelp: 'Una breve sequenza in loop per una pagina.',
      maxSeconds: 'Durata massima (secondi)',
      maxSecondsHelp: 'Da 1 a {{max}} secondi: AI la riceve come indicazione nella descrizione.',
      upTo: 'fino a {{seconds}} s',
      titleField: 'Titolo',
      titleHelp: 'Facoltativo: serve a riconoscere il lavoro e diventa il titolo della sezione nella pagina.',
      poster: 'Immagine di anteprima (poster)',
      posterAuto: 'Quando il video è pronto, AI disegna un’anteprima dalla descrizione.',
      posterChosen: 'Anteprima scelta: {{name}}',
      posterPick: 'Scegli dalla libreria',
      posterPickTitle: 'Scegli l’anteprima del video',
      posterAutoAgain: 'Lascia fare ad AI',
      start: 'Avvia la generazione',
      starting: 'Avvio…',
      another: 'Nuovo video',
      whereTitle: 'Dove sta il file',
      whereBody: 'Il video resta su AI, non nella libreria media: questa tiene fino a 10 MB per file. La pagina lo carica dall’indirizzo che AI ha dato, solo quando il visitatore preme play. AI conserva il lavoro per 24 ore; per tenere il file per sempre va scaricato e caricato a mano (MP4 fino a 10 MB).',
      recent: 'Lavori recenti',
      status: { requested: 'Richiesto', running: 'In corso', ready: 'Pronto', failed: 'Fallito' },
      running: 'AI sta preparando il video…',
      runningHelp: 'Può richiedere alcuni minuti. Puoi chiudere la finestra: il lavoro resta nell’elenco dei lavori recenti e si aggiorna quando lo riapri.',
      failedUnknown: 'AI non ha indicato il motivo.',
      posterPending: 'Sto preparando l’immagine di anteprima…',
      copy: 'Copia indirizzo',
      copied: 'Indirizzo copiato',
      insertTitle: 'Inserisci in una pagina',
      insertPage: 'Pagina',
      insertChoose: 'Scegli una pagina…',
      insert: 'Inserisci',
      inserted: 'Sezione video aggiunta alla pagina',
      insertReason: 'Video generato con AI inserito nella pagina: {{title}}',
    },
    sections: {
      title: 'Sezioni con AI',
      subtitle: 'Descrivi la pagina: AI propone le sezioni, tu scegli quali tenere. Finché non accetti, la pagina non cambia.',
      brief: 'Cosa deve dire la pagina',
      briefHelp: 'Scopo, pubblico e punti da toccare, almeno una frase. AI usa solo la conoscenza aziendale del cliente.',
      propose: 'Proponi le sezioni',
      proposeAgain: 'Proponi di nuovo',
      proposing: 'Sto preparando la proposta…',
      proposal: 'Proposta di AI',
      untouched: 'Scritta con {{account}}. La pagina non è stata toccata.',
      discardedTitle: '{{count}} sezioni scartate dai controlli',
      allDropped: 'Hai scartato tutte le sezioni.',
      redo: 'Rigenera',
      redoing: 'Rigenero…',
      drop: 'Scarta',
      replace: 'Sostituisci le sezioni attuali',
      replaceHelp: 'Senza questa scelta le sezioni accettate si aggiungono in fondo alla pagina.',
      discardAll: 'Scarta la proposta',
      accept: 'Accetta {{count}} sezioni',
      accepted: '{{count}} sezioni aggiunte alla pagina',
      acceptReason: 'Sezioni proposte da AI ({{account}}) accettate',
    },
    cta: {
      title: 'Call to action con AI',
      subtitle: 'Titolo, testo e pulsante da un obiettivo. La proposta si modifica prima di inserirla.',
      objective: 'Obiettivo',
      objectiveHelp: 'Cosa deve fare il visitatore, per esempio «richiedere un preventivo».',
      audience: 'Pubblico (facoltativo)',
      propose: 'Proponi',
      proposeAgain: 'Proponi di nuovo',
      proposing: 'Sto scrivendo…',
      proposal: 'Proposta',
      untouched: 'Scritta con {{account}}. La pagina non è stata toccata.',
      fieldTitle: 'Titolo',
      fieldText: 'Testo',
      fieldButton: 'Etichetta del pulsante',
      fieldDestination: 'Destinazione',
      fieldVariant: 'Variante',
      variants: { primary: 'Principale', secondary: 'Secondaria', soft: 'Discreta' },
      discard: 'Scarta',
      insert: 'Inserisci nella pagina',
      inserted: 'Call to action aggiunta alla pagina',
      insertReason: 'Call to action proposta da AI ({{account}}) inserita',
    },
    detail: {
      title: 'Come è stata generata',
      meta: 'Account {{account}} · modello {{model}} · {{date}}',
      earlier: 'Varianti precedenti',
      regenerate: 'Rigenera una variante',
    },
  },
}

export const aiGenEn: DictNode = {
  aiGen: {
    common: {
      account: 'AI account',
      accountHelp: 'Which subscription answers. If you do not choose, the first available one does.',
      accountDefault: 'Default (the first available)',
      accountsDegraded: 'AI did not answer the list of accounts: the default one will be used.',
      accountUnavailable: '{{label}} — unavailable: {{reason}}',
      prompt: 'Description',
      promptShort: 'At least 3 characters are needed.',
      promptLong: 'The description is too long: {{max}} characters at most.',
      aspect: 'Format',
      failed: 'Generation failed',
    },
    aspect: {
      r16x9: 'Landscape 16:9',
      r4x3: 'Landscape 4:3',
      r1x1: 'Square 1:1',
      r3x4: 'Portrait 3:4',
      r9x16: 'Portrait 9:16',
    },
    library: {
      image: 'Generate image with AI',
      video: 'Generate video with AI',
    },
    field: {
      generate: 'Generate with AI',
    },
    image: {
      title: 'Generate an image with AI',
      titleVariant: 'Regenerate a variant',
      reason: 'Image generated with AI: {{prompt}}',
      promptHelp: 'Describe subject, style and light. The text stays saved with the image.',
      name: 'Name in the library',
      nameHelp: 'Optional: if empty, the first words of the description are used.',
      alt: 'Alt text ({{lang}})',
      altHelp: 'Optional but recommended: tells people who cannot see the image what it shows.',
      archivePrevious: 'Archive the previous image',
      archivePreviousHelp: 'If the previous one is already used in a published page, leave it active: archiving removes it from the next release.',
      generate: 'Generate',
      regenerate: 'Regenerate',
      generating: 'Generating…',
      generatingHelp: 'It can take up to two minutes. If you close the window, the image still reaches the library.',
      empty: 'The preview appears here as soon as the image is ready.',
      use: 'Use this image',
      deduplicated: 'AI returned an image the library already has: no duplicate was created.',
      promptUsed: 'Description used:',
      history: 'History of variants',
      earlier: 'earlier',
      attempt: 'attempt {{n}}',
    },
    video: {
      title: 'Generate a video or clip with AI',
      reason: 'Video generated with AI: {{prompt}}',
      promptHelp: 'What must be seen and how the scene moves. A short clip means a shorter wait than a long video.',
      kind: 'What to generate',
      kinds: { video: 'Video', clip: 'Short clip' },
      videoHelp: 'A full video: the wait can be several minutes.',
      clipHelp: 'A short looping sequence for a page.',
      maxSeconds: 'Maximum length (seconds)',
      maxSecondsHelp: 'From 1 to {{max}} seconds: AI receives it as a hint in the description.',
      upTo: 'up to {{seconds}} s',
      titleField: 'Title',
      titleHelp: 'Optional: it helps recognise the job and becomes the section title on the page.',
      poster: 'Poster image',
      posterAuto: 'When the video is ready, AI draws a poster from the description.',
      posterChosen: 'Poster chosen: {{name}}',
      posterPick: 'Choose from the library',
      posterPickTitle: 'Choose the video poster',
      posterAutoAgain: 'Let AI do it',
      start: 'Start generating',
      starting: 'Starting…',
      another: 'New video',
      whereTitle: 'Where the file lives',
      whereBody: 'The video stays on AI, not in the media library, which keeps files up to 10 MB. The page loads it from the address AI gave, only when the visitor presses play. AI keeps the job for 24 hours; to keep the file for ever, download it and upload it by hand (MP4 up to 10 MB).',
      recent: 'Recent jobs',
      status: { requested: 'Requested', running: 'Running', ready: 'Ready', failed: 'Failed' },
      running: 'AI is preparing the video…',
      runningHelp: 'It can take several minutes. You can close the window: the job stays in the recent jobs list and updates when you reopen it.',
      failedUnknown: 'AI did not say why.',
      posterPending: 'Preparing the poster image…',
      copy: 'Copy address',
      copied: 'Address copied',
      insertTitle: 'Insert into a page',
      insertPage: 'Page',
      insertChoose: 'Choose a page…',
      insert: 'Insert',
      inserted: 'Video section added to the page',
      insertReason: 'Video generated with AI inserted into the page: {{title}}',
    },
    sections: {
      title: 'Sections with AI',
      subtitle: 'Describe the page: AI proposes the sections, you choose which to keep. Until you accept, the page does not change.',
      brief: 'What the page must say',
      briefHelp: 'Purpose, audience and points to cover, at least one sentence. AI uses only the client’s company knowledge.',
      propose: 'Propose sections',
      proposeAgain: 'Propose again',
      proposing: 'Preparing the proposal…',
      proposal: 'AI proposal',
      untouched: 'Written with {{account}}. The page has not been touched.',
      discardedTitle: '{{count}} sections dropped by the checks',
      allDropped: 'You dropped every section.',
      redo: 'Regenerate',
      redoing: 'Regenerating…',
      drop: 'Drop',
      replace: 'Replace the current sections',
      replaceHelp: 'Without this, the accepted sections are added at the end of the page.',
      discardAll: 'Drop the proposal',
      accept: 'Accept {{count}} sections',
      accepted: '{{count}} sections added to the page',
      acceptReason: 'Sections proposed by AI ({{account}}) accepted',
    },
    cta: {
      title: 'Call to action with AI',
      subtitle: 'Title, text and button from an objective. The proposal can be edited before it goes in.',
      objective: 'Objective',
      objectiveHelp: 'What the visitor must do, for example “ask for a quote”.',
      audience: 'Audience (optional)',
      propose: 'Propose',
      proposeAgain: 'Propose again',
      proposing: 'Writing…',
      proposal: 'Proposal',
      untouched: 'Written with {{account}}. The page has not been touched.',
      fieldTitle: 'Title',
      fieldText: 'Text',
      fieldButton: 'Button label',
      fieldDestination: 'Destination',
      fieldVariant: 'Variant',
      variants: { primary: 'Primary', secondary: 'Secondary', soft: 'Soft' },
      discard: 'Drop',
      insert: 'Insert into the page',
      inserted: 'Call to action added to the page',
      insertReason: 'Call to action proposed by AI ({{account}}) inserted',
    },
    detail: {
      title: 'How it was generated',
      meta: 'Account {{account}} · model {{model}} · {{date}}',
      earlier: 'Earlier variants',
      regenerate: 'Regenerate a variant',
    },
  },
}
